import IntakeCallSchedulerModal from "@core/scheduler-modals/IntakeCallSchedulerModal";
import { api } from "@core/services/nocd-api";
import Button from "@core/ui/Button";
import { CheckCircleIcon, ChevronDownIcon } from "@heroicons/react/solid";
import cn from "classnames";
import Image from "next/image";
import HeroImage from "public/images/support/contact-us-hero.png";
import { ReactNode, useState } from "react";
import { useForm } from "react-hook-form";
import toast from "react-hot-toast";

import styles from "./styles.module.css";

function Question({
  question,
  answer,
}: {
  question: ReactNode;
  answer: ReactNode;
}): JSX.Element {
  const [isOpen, setIsOpen] = useState(false);

  return (
    <div className="py-5">
      <button
        className="font-semibold text-left flex justify-between w-full"
        type="button"
        onClick={() => setIsOpen((prev) => !prev)}
      >
        <span className="font-poppins">{question}</span>

        <ChevronDownIcon
          className={cn(
            "w-5 h-5 transform duration-300",
            isOpen && "text-v2-teal-600 rotate-180"
          )}
        />
      </button>

      {isOpen ? <p className="mt-2">{answer}</p> : null}
    </div>
  );
}

const FAQs = [
  {
    question: "Do you treat any conditions other than OCD?",
    answer: (
      <>
        Every NOCD therapist is trained to treat depressive and anxiety
        disorders. In addition, many of our therapists specialize in treating a
        number of OCD-related conditions such as body focused repetitive
        behaviors (dermatillomania/excoriation disorder, trichotillomania),
        hoarding disorder, tic disorders, body dysmorphic disorder, as well as
        PTSD.
      </>
    ),
  },
  {
    question: "Do you accept insurance?",
    answer: (
      <>
        NOCD is-in network with several major health insurance plans around the
        US. In-network coverage varies by state and is dependent on an
        individual&apos;s mental health benefits. Once we get you scheduled for
        your first session, we&apos;ll have you upload your insurance card
        information and our Billing team will verify your insurance eligibility.
        Your health plan will be able to confirm any amounts that you would be
        responsible for.{" "}
        <a
          className="text-v2-teal-600"
          href={`${process.env.NEXT_PUBLIC_WEBSITE_BASE_URL}/about-us/billing-resources`}
        >
          Learn more about billing at NOCD.
        </a>
      </>
    ),
  },
  {
    question: "Do you treat every subtype of OCD?",
    answer: (
      <>
        Yes, every therapist here is trained to treat all subtypes of OCD. While
        each theme of OCD may present differently, the cycle of OCD remains the
        same, and Exposure and Response Prevention (ERP) therapy is designed to
        address this cycle. Your NOCD therapist will create a personalized
        treatment plan to address your individual needs.
      </>
    ),
  },
  {
    question: "How did NOCD start?",
    answer: (
      <>
        <span className="block">
          NOCD was founded by Stephen Smith after his struggle with undiagnosed
          OCD. Once he found Exposure and Response Prevention (ERP) therapy, he
          realized how hard it was to access effective, affordable treatment.
          This inspired him to create NOCD, making specialized OCD care
          accessible worldwide.
        </span>

        <a
          href={`${process.env.NEXT_PUBLIC_WEBSITE_BASE_URL}/about-us/our-story`}
          className="block text-right text-v2-teal-600"
        >
          Read the full story here
        </a>
      </>
    ),
  },
  {
    question: "Do you treat children?",
    answer: (
      <>
        Yes! Many of our therapists are trained to treat OCD in children as
        young as 5 years old.
      </>
    ),
  },
  {
    question: "Do you have therapists in all 50 states?",
    answer: <>Yes, we have therapists licensed in all 50 states.</>,
  },
  {
    question: "How are your therapists trained?",
    answer: (
      <>
        Every NOCD Therapist completes an intensive, expert-designed program in
        ERP therapy, the nuances of OCD, and the identification and treatment of
        related conditions. Before seeing NOCD Members, therapists must
        demonstrate mastery of their training and pass multiple exams. NOCD
        Therapists receive continuous education and consultation from clinical
        leaders. Our treatment model is led by NOCD&apos;s Chief Clinical
        Officer{" "}
        <a
          href={`${process.env.NEXT_PUBLIC_WEBSITE_BASE_URL}/clinical-leadership/patrick-mcgrath`}
          className="text-v2-teal-600"
        >
          Dr. Patrick McGrath
        </a>
        , who has over 20 years of OCD treatment experience.
      </>
    ),
  },
  {
    question: "Do you refer Members for medication management?",
    answer: (
      <>
        Yes, our therapists regularly refer Members to qualified psychiatrists
        with experience treating OCD. Learn more about{" "}
        <a
          href={`${process.env.NEXT_PUBLIC_WEBSITE_BASE_URL}/medication`}
          className="text-v2-teal-600"
        >
          medication management for OCD
        </a>
        .
      </>
    ),
  },
  {
    question: "Do I get to pick my therapist?",
    answer: (
      <>
        Our Member Advocates Team will help match you with a therapist based on
        your individual needs and preferences. Therapist profiles can be shared
        during your intake call with us, or you can view a list of{" "}
        <a
          href={`${process.env.NEXT_PUBLIC_WEBSITE_BASE_URL}/therapists`}
          className="text-v2-teal-600"
        >
          therapists in your state.
        </a>
      </>
    ),
  },
  {
    question: "How much does NOCD Therapy cost?",
    answer: (
      <>
        We work with major insurance providers in all 50 states. NOCD Therapy
        Members using insurance save an average of 79% and pay an average copay
        of under $14 when starting NOCD therapy.
      </>
    ),
  },
];

function Faqs(): JSX.Element {
  return (
    <div className="border border-v2-gray-200 rounded-2xl overflow-hidden max-w-[55.75rem] mx-auto px-6 tablet:px-8">
      <h2 className="pt-6 text-24px font-bold">FAQs</h2>

      <div className="divide-y divide-v2-gray-200">
        {FAQs.map((q) => (
          <Question key={q.question} question={q.question} answer={q.answer} />
        ))}
      </div>
    </div>
  );
}

type ContactFormCategory =
  | "canceling"
  | "scheduling_future_session"
  | "records_request"
  | "billing"
  | "changing_therapist"
  | "non_member_inquiry";

const CONTACT_FORM_CATEGORIES: { value: ContactFormCategory; label: string }[] =
  [
    { value: "canceling", label: "Canceling" },
    { value: "scheduling_future_session", label: "Scheduling future session" },
    { value: "records_request", label: "Records Request" },
    { value: "billing", label: "Billing – Balance/Invoice" },
    { value: "changing_therapist", label: "Changing Therapist" },
    { value: "non_member_inquiry", label: "Non-Member Inquiry" },
  ];

interface FormValues {
  name: string;
  email: string;
  message: string;
  category: ContactFormCategory;
}

export default function SupportScreen(): JSX.Element {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const { register, handleSubmit, formState } = useForm<FormValues>();

  const onSubmit = handleSubmit(async ({ name, email, message, category }) => {
    await toast.promise(
      api.post("/v1/marketing/website_contact_form", {
        name,
        email,
        message,
        category,
      }),
      {
        loading: "Submitting...",
        success: "Thanks for reaching out. We'll be in touch soon!",
        error: (error: Error) => error?.message || "An unknown error occurred",
      }
    );
  });

  return (
    <>
      <main className={styles.content}>
        <div
          className="px-6 py-10 tablet:py-16"
          style={{
            background:
              "linear-gradient(180deg, #FFF 0%, rgba(255, 255, 255, 0.00) 68.86%), linear-gradient(172deg, rgba(255, 255, 255, 0.00) 6.4%, #FFF 100%), linear-gradient(212deg, #FFF 22.13%, #E8D7FF 82.07%)",
          }}
        >
          <section className="grid tablet:grid-cols-2 items-center gap-8 max-w-[845px] mx-auto">
            <div className="tablet:max-w-[26rem] text-center tablet:text-left order-2 tablet:order-1">
              <h1 className="font-bold text-32px tablet:text-[3rem] leading-tight">
                Contact Us
              </h1>

              <p className="leading-snug mt-6">
                Contact us by filling out the form below. We&apos;ll respond via
                email within 2 business days.
              </p>
            </div>

            <div className="flex w-full justify-start tablet:justify-end order-1 tablet:order-2">
              <Image
                src={HeroImage}
                alt="Contact us"
                width={337}
                height={337}
              />
            </div>
          </section>
        </div>

        <div
          style={{
            background: "url('/images/support/gradient-bg.svg')",
            backgroundPosition: "top center",
            backgroundRepeat: "no-repeat",
            backgroundSize: "cover",
          }}
        >
          <div className="px-6 py-[42px]">
            <div className="max-w-[845px] mx-auto p-6 rounded-2xl bg-white">
              {formState?.isSubmitSuccessful ? (
                <div className="my-20 flex flex-col items-center space-y-8">
                  <CheckCircleIcon className="w-20 h-20 text-teal-600" />

                  <p className="max-w-160 text-center text-18px font-bold leading-loose">
                    Thanks for reaching out. We&apos;ll be in touch soon!
                  </p>
                </div>
              ) : (
                <>
                  <h2 className="font-bold text-22px tablet:text-24px mb-4 text-center">
                    We&apos;d love to hear from you!
                  </h2>
                  <form onSubmit={onSubmit}>
                    <fieldset className="mx-auto mb-8 grid grid-cols-1 gap-3 font-manrope bg-white">
                      <label htmlFor="category">
                        <span className="block font-bold">
                          What can we help you with?
                        </span>
                        <select
                          id="category"
                          defaultValue=""
                          {...register("category", {
                            required: "Please select an option",
                          })}
                        >
                          <option value="" disabled>
                            Select an option
                          </option>
                          {CONTACT_FORM_CATEGORIES.map(({ value, label }) => (
                            <option key={value} value={value}>
                              {label}
                            </option>
                          ))}
                        </select>
                        <p className="block text-red-600">
                          {formState?.errors?.category
                            ? "Please select an option"
                            : ""}
                        </p>
                      </label>

                      <label htmlFor="name">
                        <span className="block font-bold">Name</span>
                        <input
                          type="text"
                          id="name"
                          placeholder="Name"
                          {...register("name", {
                            required: "Name is required",
                          })}
                        />
                        <p className="block text-red-600">
                          {formState?.errors?.name ? "Name is required" : ""}
                        </p>
                      </label>

                      <label htmlFor="email">
                        <span className="block font-bold">Email</span>
                        <input
                          type="email"
                          name="email"
                          id="email"
                          placeholder="Email"
                          {...register("email", {
                            required: "Email is required",
                          })}
                        />
                        <p className="block text-red-600">
                          {formState?.errors?.email ? "Email is required" : ""}
                        </p>
                      </label>

                      <label htmlFor="message">
                        <span className="block font-bold">Message</span>
                        <textarea
                          name="message"
                          id="message"
                          rows={6}
                          placeholder="Message"
                          {...register("message", { required: true })}
                        />
                        <p className="block text-red-600">
                          {formState?.errors?.message
                            ? "Message is required"
                            : ""}
                        </p>
                      </label>
                    </fieldset>

                    <div className="flex justify-end">
                      <Button
                        type="submit"
                        color="indigo"
                        className="!h-13 !font-semibold !rounded-lg"
                      >
                        Submit
                      </Button>
                    </div>
                  </form>
                </>
              )}
            </div>

            <div className="mt-6 bg-white rounded-2xl max-w-[845px] mx-auto">
              <Faqs />
            </div>
          </div>
        </div>
      </main>

      <IntakeCallSchedulerModal
        isOpen={isModalOpen}
        handleClose={() => setIsModalOpen(false)}
        source="web-app-contact-page"
      />
    </>
  );
}
